import React, { useState, useEffect } from 'react';
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Tabs,
  Tab,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  ExitToApp as ExitToAppIcon,
  Download as DownloadIcon,
  List as ListIcon,
  Timeline as TimelineIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { onAuthStateChange, signOutUser, getCurrentUser } from './firebase/authService';
import { getEvents, addEvent, updateEvent, deleteEvent, exportEventsToCSV, exportEventsToJSON } from './firebase/firestoreService';
import Auth from './components/Auth/Auth';
import EventForm from './components/EventForm/EventForm';
import EventList from './components/EventList/EventList';
import TimelineView from './components/TimelineView/TimelineView';
import CalendarView from './components/CalendarView/CalendarView';
import AnniversariesView from './components/AnniversariesView/AnniversariesView';
import SearchAndFilter from './components/SearchAndFilter/SearchAndFilter';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [view, setView] = useState(0); // 0: List, 1: Timeline, 2: Calendar, 3: Anniversaries
  const [eventFormOpen, setEventFormOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [filters, setFilters] = useState({});
  const [sortBy, setSortBy] = useState('eventDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChange((user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      loadEvents();
    } else {
      setEvents([]);
      setFilteredEvents([]);
    }
  }, [user]);

  useEffect(() => {
    applyFilters();
  }, [events, filters]);

  const loadEvents = async () => {
    try {
      const eventsData = await getEvents(user.uid, filters);
      setEvents(eventsData);
    } catch (error) {
      console.error('Error loading events:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...events];

    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase();
      filtered = filtered.filter(event =>
        event.title?.toLowerCase().includes(searchLower) ||
        event.description?.toLowerCase().includes(searchLower) ||
        event.location?.toLowerCase().includes(searchLower) ||
        event.tags?.some(tag => tag.toLowerCase().includes(searchLower))
      );
    }

    if (filters.category) {
      filtered = filtered.filter(event => event.category === filters.category);
    }

    if (filters.startDate) {
      filtered = filtered.filter(event => {
        if (!event.eventDate) return false;
        return new Date(event.eventDate) >= filters.startDate;
      });
    }

    if (filters.endDate) {
      filtered = filtered.filter(event => {
        if (!event.eventDate) return false;
        return new Date(event.eventDate) <= filters.endDate;
      });
    }

    if (filters.tags && filters.tags.length > 0) {
      const filterTagsLower = filters.tags.map(t => t.toLowerCase());
      filtered = filtered.filter(event =>
        event.tags && event.tags.some(tag => filterTagsLower.includes(tag.toLowerCase()))
      );
    }

    setFilteredEvents(filtered);
  };

  const handleAddEvent = () => {
    setEditingEvent(null);
    setEventFormOpen(true);
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setEventFormOpen(true);
  };

  const handleDeleteEvent = async (eventId) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await deleteEvent(eventId);
        await loadEvents();
      } catch (error) {
        console.error('Error deleting event:', error);
        alert('Error deleting event. Please try again.');
      }
    }
  };

  const handleSubmitEvent = async (eventData) => {
    try {
      // Event data already has documentUrl (either URL or base64)
      const finalEventData = {
        ...eventData,
        userId: user.uid
      };

      if (editingEvent) {
        await updateEvent(editingEvent.id, finalEventData);
      } else {
        await addEvent(finalEventData);
      }
      setEventFormOpen(false);
      setEditingEvent(null);
      await loadEvents();
    } catch (error) {
      console.error('Error saving event:', error);
      alert(`Error saving event: ${error.message || 'Please try again.'}`);
    }
  };

  const handleExport = (format) => {
    const data = format === 'csv' ? exportEventsToCSV(filteredEvents) : exportEventsToJSON(filteredEvents);
    const blob = new Blob([data], { type: format === 'csv' ? 'text/csv' : 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `life-events.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    try {
      await signOutUser();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleSort = (field, order) => {
    setSortBy(field);
    setSortOrder(order);
  };

  const getAllTags = () => {
    const tagMap = new Map(); // Map of lowercase -> original case
    events.forEach(event => {
      if (event.tags) {
        event.tags.forEach(tag => {
          const lowerTag = tag.toLowerCase();
          // Keep the first occurrence's original case, or prefer capitalized version
          if (!tagMap.has(lowerTag)) {
            tagMap.set(lowerTag, tag);
          } else {
            // If current tag is capitalized and stored one isn't, prefer capitalized
            const stored = tagMap.get(lowerTag);
            if (tag[0] === tag[0].toUpperCase() && stored[0] === stored[0].toLowerCase()) {
              tagMap.set(lowerTag, tag);
            }
          }
        });
      }
    });
    return Array.from(tagMap.values()).sort();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Auth />;
  }

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Big Life Events
          </Typography>
          <Button
            color="inherit"
            startIcon={<AddIcon />}
            onClick={handleAddEvent}
            sx={{ mr: 2 }}
          >
            Add Event
          </Button>
          <IconButton
            color="inherit"
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            <DownloadIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem onClick={() => handleExport('csv')}>Export as CSV</MenuItem>
            <MenuItem onClick={() => handleExport('json')}>Export as JSON</MenuItem>
          </Menu>
          <Button
            color="inherit"
            startIcon={<ExitToAppIcon />}
            onClick={handleSignOut}
          >
            Sign Out
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={view} onChange={(e, newValue) => setView(newValue)}>
            <Tab icon={<ListIcon />} label="List" />
            <Tab icon={<TimelineIcon />} label="Timeline" />
            <Tab icon={<CalendarIcon />} label="Calendar" />
          </Tabs>
        </Box>

        <SearchAndFilter
          filters={filters}
          onFilterChange={setFilters}
          allTags={getAllTags()}
        />

        {view === 0 && (
          <EventList
            events={filteredEvents}
            onEdit={handleEditEvent}
            onDelete={handleDeleteEvent}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
        )}

        {view === 1 && (
          <TimelineView
            events={filteredEvents}
            onEdit={handleEditEvent}
            onDelete={handleDeleteEvent}
          />
        )}

        {view === 2 && (
          <CalendarView
            events={filteredEvents}
            onEdit={handleEditEvent}
            onDelete={handleDeleteEvent}
          />
        )}

        {view === 3 && (
          <AnniversariesView
            events={events}
            onEdit={handleEditEvent}
            onDelete={handleDeleteEvent}
          />
        )}
      </Container>

      <EventForm
        open={eventFormOpen}
        onClose={() => {
          setEventFormOpen(false);
          setEditingEvent(null);
        }}
        onSubmit={handleSubmitEvent}
        event={editingEvent}
        userId={user?.uid}
      />
    </Box>
  );
}

export default App;

